import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'glass' | 'gradient' | 'bordered';
  hover?: boolean;
  className?: string;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  hover = false,
  className,
  onClick,
}) => {
  const baseStyles = `
    rounded-2xl
    transition-all duration-300 ease-out
  `;

  const variants = {
    default: `
      bg-[var(--bg-secondary)]
      border border-[var(--border-color)]
    `,
    glass: `
      bg-[var(--glass-bg)]
      backdrop-blur-xl
      border border-[var(--glass-border)]
      shadow-[var(--glass-shadow)]
    `,
    gradient: `
      bg-gradient-to-br from-[var(--bg-secondary)] to-[var(--bg-tertiary)]
      border border-[var(--border-color)]
    `,
    bordered: `
      bg-transparent
      border-2 border-[var(--border-color)]
    `,
  };

  const hoverStyles = hover
    ? `
      cursor-pointer
      hover:border-[var(--border-color-hover)]
      hover:shadow-xl
      hover:-translate-y-1
      hover:bg-[var(--bg-card-hover)]
    `
    : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={hover ? { scale: 1.02 } : undefined}
      className={clsx(baseStyles, variants[variant], hoverStyles, className)}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
};

// Card subcomponents
export const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={clsx('px-6 py-4 border-b border-[var(--border-color)]', className)}>
    {children}
  </div>
);

export const CardBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={clsx('px-6 py-4', className)}>{children}</div>;

export const CardFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={clsx('px-6 py-4 border-t border-[var(--border-color)]', className)}>
    {children}
  </div>
);

export default Card;
